// src/pages/mission/Mission.hooks.js
// Custom hooks for mission page

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOADOUT } from './Mission.constants';

/**
 * Hook to manage mission state from route or props
 */
export const useMissionData = (propMission, propSquad, propTheme) => {
  const location = useLocation();
  
  const { 
    mission: stateMission, 
    squad: stateSquad, 
    campaign: stateCampaign, // ADDED: Get campaign from location state
    campaignId, 
    campaignName,
    day,
    mode,
    theme: stateTheme 
  } = location.state || {};
  
  const mission = stateMission || propMission;
  const squad = stateSquad || propSquad;
  const theme = stateTheme || propTheme || 'vanilla';
  const currentTheme = location.pathname.split('/')[1] || theme;
  
  return {
    mission,
    squad,
    campaign: stateCampaign, // ADDED: Return campaign object
    theme: currentTheme,
    campaignId,
    campaignName,
    day,
    mode
  };
};

/**
 * Hook to manage squad loadouts
 */
export const useSquadLoadouts = (squad) => {
  const [squadLoadouts, setSquadLoadouts] = useState([]);

  useEffect(() => {
    if (squad && squad.length > 0) {
      setSquadLoadouts(squad.map(s => ({
        soldierId: s.id,
        ...DEFAULT_LOADOUT
      })));
    }
  }, [squad]);

  const updateLoadout = (soldierId, slot, itemId) => {
    setSquadLoadouts(prev => prev.map(loadout => 
      loadout.soldierId === soldierId
        ? { ...loadout, [slot]: itemId }
        : loadout
    ));
  };

  const getLoadout = (soldierId) => {
    return squadLoadouts.find(l => l.soldierId === soldierId);
  };

  return {
    squadLoadouts,
    updateLoadout,
    getLoadout
  };
};

/**
 * Hook to manage soldier selection
 */
export const useSelectedSoldier = (squad) => {
  const [selectedSoldier, setSelectedSoldier] = useState(null);

  useEffect(() => {
    if (squad && squad.length > 0) {
      setSelectedSoldier(squad[0]);
    }
  }, [squad]);

  return {
    selectedSoldier,
    setSelectedSoldier
  };
};

/**
 * Hook to handle mission navigation
 */
export const useMissionNavigation = (
  currentTheme, 
  campaign, // CHANGED: Receive full campaign object instead of just campaignId
  campaignId,
  onStartBattle, 
  onAbort
) => {
  const navigate = useNavigate();

  const handleStartBattle = (displayMission, squadWithLoadouts) => {
    console.log('🎮 Starting battle with:', {
      mission: displayMission.name,
      squad: squadWithLoadouts.map(s => s.name),
      loadouts: squadWithLoadouts.map(s => s.loadout),
      campaign: campaign, // ADDED: Log campaign for debugging
      theme: currentTheme
    });
    
    if (onStartBattle) {
      onStartBattle(squadWithLoadouts);
    } else {
      navigate(`/${currentTheme}/battle`, {
        state: {
          campaign: campaign, // FIXED: Pass the full campaign object
          mission: displayMission,
          squad: squadWithLoadouts,
          campaignId: campaignId,
          theme: currentTheme
        }
      });
    }
  };

  const handleAbort = () => {
    if (onAbort) {
      onAbort();
    } else {
      navigate(`/${currentTheme}/base`, {
        state: {
          campaign: campaign, // FIXED: Pass campaign object back to base
          campaignId: campaignId,
          theme: currentTheme
        }
      });
    }
  };

  return {
    handleStartBattle,
    handleAbort
  };
};

/**
 * Debug logging hook
 */
export const useMissionDebug = (mission, squad, currentTheme, campaign) => {
  useEffect(() => {
    console.log('📋 Mission.jsx received:', {
      hasMission: !!mission,
      hasSquad: !!squad,
      hasCampaign: !!campaign, // ADDED: Log campaign presence
      squadCount: squad?.length || 0,
      missionName: mission?.name,
      campaignId: campaign?.id,
      theme: currentTheme,
      squadDetails: squad?.map(s => ({
        name: s.name,
        nickname: s.nickname,
        rank: s.rank,
        class: s.class,
        health: s.health,
        experience: s.experience
      }))
    });
  }, [mission, squad, currentTheme, campaign]);
};